import { Controller } from "@hotwired/stimulus";
import { useClickOutside, useTransition } from "stimulus-use";

import {
  autoUpdate,
  computePosition,
  flip,
  shift,
  limitShift,
  offset,
  inline,
} from "@floating-ui/dom";

// Connects to data-controller="dropdown"
export default class extends Controller {
  static targets = ["button", "menu"];
  async connect() {
    useClickOutside(this);
    useTransition(this, { element: this.menuTarget });

    // Add event listener for escape key
    document.addEventListener("keydown", this.handleKeyDown.bind(this));

    const placement = this.element.dataset.placement || "bottom-end";
    const offsetValue = this.element.dataset.offset
      ? parseInt(this.element.dataset.offset)
      : -10;

    const updatePosition = async () => {
      const position = await computePosition(
        this.buttonTarget,
        this.menuTarget,
        {
          placement: placement,
          middleware: [
            flip(),
            shift({ limiter: limitShift() }),
            offset(offsetValue),
            inline(),
          ],
        },
      );

      Object.assign(this.menuTarget.style, {
        left: `${Math.max(position.x, 10)}px`,
        top: `${Math.max(position.y, 10)}px`,
        right: `${Math.max(window.innerWidth - position.x - this.menuTarget.offsetWidth, 10)}px`,
      });
    };

    const cleanup = autoUpdate(
      this.buttonTarget,
      this.menuTarget,
      updatePosition,
    );
  }

  disconnect() {
    // Remove event listener when controller is disconnected
    document.removeEventListener("keydown", this._boundHandleKeyDown);
  }

  initialize() {
    // Store bound method reference to use for both adding and removing the event listener
    this._boundHandleKeyDown = this.handleKeyDown.bind(this);
  }

  handleKeyDown(event) {
    // Close dropdown when Escape key is pressed
    if (
      event.key === "Escape" &&
      !this.menuTarget.classList.contains("hidden")
    ) {
      this.leave();
    }
  }

  toggle() {
    if (this.menuTarget.classList.contains("open")) {
      this.menuTarget.classList.remove("open");
    } else {
      this.menuTarget.classList.add("open");
    }
    this.toggleTransition();
  }

  open() {
    this.enter();
  }

  close(event) {
    event.preventDefault();
    this.leave();
  }

  clickOutside(event) {
    this.leave();
  }
}
